import { Loader2 } from "lucide-react";

export const Spinner = ({ className = "" }) => <Loader2 className={`h-5 w-5 animate-spin text-emerald-400 ${className}`} />;

export const PageHeader = ({ eyebrow, title, description, actions }) => (
  <div className="am-rise mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
    <div className="max-w-2xl">
      <div className="cc-eyebrow mb-3 text-emerald-400/80">{eyebrow}</div>
      <h1 className="font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">{title}</h1>
      {description && <p className="mt-4 text-sm leading-relaxed text-slate-400 md:text-base">{description}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
  </div>
);

export const Panel = ({ title, icon: Icon, right, children, className = "", testid }) => (
  <section data-testid={testid} className={`cc-card am-rise p-6 ${className}`}>
    {(title || right) && (
      <div className="mb-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className="h-4 w-4 text-emerald-400" />}
          <h2 className="font-display text-base font-bold uppercase tracking-wider text-slate-100 md:text-lg">{title}</h2>
        </div>
        {right}
      </div>
    )}
    {children}
  </section>
);

export const EmptyState = ({ icon: Icon, title, description, children, testid }) => (
  <div data-testid={testid} className="cc-card cc-grid-bg am-rise flex flex-col items-start gap-4 p-10">
    {Icon && (
      <div className="grid h-12 w-12 place-items-center rounded-md border border-emerald-500/30 bg-emerald-500/10">
        <Icon className="h-5 w-5 text-emerald-400" />
      </div>
    )}
    <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-100">{title}</h3>
    {description && <p className="max-w-xl text-sm leading-relaxed text-slate-400">{description}</p>}
    {children}
  </div>
);

const TONES = {
  emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  amber: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  red: "border-red-500/30 bg-red-500/10 text-red-300",
  cyan: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300",
  muted: "border-slate-700 bg-slate-800/40 text-slate-400",
};

export const Tag = ({ tone = "muted", children, testid, className = "" }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider ${TONES[tone]} ${className}`}>
    {children}
  </span>
);

export const Stat = ({ label, value, testid }) => (
  <div className="cc-card p-5" data-testid={testid}>
    <div className="cc-eyebrow">{label}</div>
    <div className="mt-2 font-mono text-xl font-bold tracking-wide text-slate-50 sm:text-2xl">{value}</div>
  </div>
);

export const Alert = ({ tone = "red", title, children, testid }) => (
  <div data-testid={testid} className={`rounded-md border p-4 text-sm ${TONES[tone]}`}>
    {title && <div className="mb-1 font-semibold">{title}</div>}
    <div className="leading-relaxed opacity-90">{children}</div>
  </div>
);

export const WorkingTerminal = ({ title, lines = [], testid }) => (
  <div data-testid={testid} className="cc-card am-rise overflow-hidden">
    <div className="relative h-0.5 w-full overflow-hidden bg-slate-800">
      <div className="am-scan absolute inset-y-0 w-1/3 bg-emerald-400" />
    </div>
    <div className="p-6 font-mono text-xs leading-6 text-slate-400">
      <div className="mb-2 flex items-center gap-2 text-emerald-300"><Spinner className="h-4 w-4" />{title}</div>
      {lines.map((l) => <div key={l}>&gt; {l}</div>)}
    </div>
  </div>
);
